<template>
  <div>
    <!-- ── Main Import Dialog ── -->
    <v-dialog persistent width="900" v-model="importDialog">
      <v-card class="custom-card" rounded="0">
        <v-toolbar color="secondary" class="white--text">
          <v-toolbar-title class="font-weight-bold">{{ $t('farm.importFarms') }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon class="white--text" :disabled="importing || validating" @click="toggleImportDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <div style="position: relative;">
          <!-- Loading state (validating pre-flight) -->
          <div
            v-if="validating"
            class="d-flex align-center justify-center white flex-column px-4 py-5"
            style="min-height: 240px;"
          >
            <v-progress-circular :size="60" color="primary" indeterminate></v-progress-circular>
            <p class="mt-8 text-center text--secondary">
              {{ $t('farm.validatingRecords') || 'Validating records — checking coordinates, boundaries & duplicates…' }}
            </p>
          </div>

          <!-- Loading state (actual import) -->
          <div
            v-else-if="importing"
            class="d-flex align-center justify-center white flex-column px-4 py-5"
            style="min-height: 240px;"
          >
            <v-progress-circular :size="75" color="primary" indeterminate></v-progress-circular>
            <p class="mt-10 text-center">{{ $t('farm.importingNote') }}</p>
          </div>

          <!-- Normal content -->
          <div v-show="!importing && !validating">
            <div class="px-4 py-3" v-if="!bulkImportDetail">
              <v-card elevation="0">
                <v-tabs class="tabs-pill" background-color="grey lighten-3" v-model="tab">
                  <v-tab class="text-capitalize" @click="type = 'import'">{{ $t('oma.import') }}</v-tab>
                  <v-tab class="text-capitalize" @click="type = 'history'">{{ $t('oma.history') }}</v-tab>
                </v-tabs>
              </v-card>
              <v-tabs-items v-model="tab">
                <v-tab-item key="import">
                  <FarmBulkImportTab
                    :format="format"
                    @cancelImport="toggleImportDialog"
                    @importFile="handleFileSelected"
                  />
                </v-tab-item>
                <v-tab-item key="history">
                  <FarmBulkImportHistoryTab />
                </v-tab-item>
              </v-tabs-items>
            </div>
            <div v-else>
              <FarmBulkImportDetail :defaultDetail="bulkImportDetail" @completed="restartProcess" />
            </div>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <!-- ── Staging / Review Dialog ── -->
    <FarmBulkImportStagingDialog
      v-model="stagingDialogOpen"
      :stagingData="stagingData"
      :loading="importing"
      @back="closeStagingGoBack"
      @cancel="handleCancel"
      @confirm="handleConfirmImport"
    />
  </div>
</template>

<script>
import FarmBulkImportTab from './FarmBulkImportTab';
import FarmBulkImportHistoryTab from './FarmBulkImportHistoryTab';
import FarmBulkImportDetail from './FarmBulkImportDetail';
import FarmBulkImportStagingDialog from './FarmBulkImportStagingDialog';
import axios from 'axios';

export default {
  components: {
    FarmBulkImportTab,
    FarmBulkImportHistoryTab,
    FarmBulkImportDetail,
    FarmBulkImportStagingDialog,
  },

  props: {
    value: {
      type: Boolean,
      required: true,
    },
    format: {
      type: String,
      required: true,
    },
  },

  computed: {
    importDialog: {
      get() { return this.value; },
      set() { this.$emit('input', !this.value); },
    },
  },

  data() {
    return {
      importing: false,
      validating: false,
      bulkImportDetail: null,
      tab: 'import',
      hasImported: false,

      // Staging
      stagingDialogOpen: false,
      stagingData: [],       // Array of validated row objects
      pendingFile: null,     // The File object held until user confirms
      pendingCallback: null, // Optional callback from FarmBulkImportTab
    };
  },

  methods: {
    toggleImportDialog() {
      if (!this.importing && !this.validating) {
        const isOpen = this.value;
        this.importDialog = !isOpen;
        if (isOpen) {
          if (this.hasImported) {
            this.hasImported = false;
            this.$emit('bulkImportedFarms');
          }
          this.restartProcess();
        }
      }
    },

    /**
     * Step 1 — called when the user hits "Import" in FarmBulkImportTab.
     * Instead of uploading immediately, we hit the /validate endpoint first
     * and open the staging dialog.
     */
    async handleFileSelected(file, callback) {
      this.pendingFile = file;
      this.pendingCallback = callback || null;

      try {
        this.validating = true;
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post('/farm/bulk-upload/validate', formData);
        const data = response.data;

        if (data.success) {
          this.stagingData = data.data; // array of validated row objects
          // Close main import dialog body visually, open staging
          this.validating = false;
          this.importDialog = false;   // hide main dialog
          this.stagingDialogOpen = true;
        } else {
          this.$notify({
            type: 'error',
            title: data.message || this.$t('serverError'),
          });
        }
      } catch (error) {
        console.error(error);
        this.$notify({
          type: 'error',
          title: this.$t('serverError'),
        });
      } finally {
        this.validating = false;
      }
    },

    /**
     * Step 2 — user hits "Import N Records" in the staging dialog.
     * We send the approved rows and any user edits (coordinate / polygon fixes) to the
     * real upload endpoint so the server applies them before processing.
     *
     * @param {Array}  approvedRows - rows that passed validation (no hard errors)
     * @param {Object} rowEdits     - { [rowIndex]: { lat?, lng?, geofence? } }
     */
    async handleConfirmImport(approvedRows, rowEdits = {}) {
      try {
        this.importing = true;
        this.stagingDialogOpen = false;
        this.importDialog = true;  // re-show main dialog to display spinner

        const formData = new FormData();
        formData.append('file', this.pendingFile);
        // Approved row indices — backend skips rows not in this list
        formData.append('approvedRowIndices', JSON.stringify(approvedRows.map(r => r.rowIndex)));
        // User-edited overrides: coordinate fixes + polygon fixes
        if (Object.keys(rowEdits).length) {
          formData.append('rowEdits', JSON.stringify(rowEdits));
        }

        const response = await axios.post('/farm/bulk-upload', formData);
        const data = response.data;

        if (data.success) {
          this.bulkImportDetail = data.data;
          this.hasImported = true;
          if (typeof this.pendingCallback === 'function') {
            this.pendingCallback();
          }
        } else {
          this.$notify({
            type: 'error',
            title: data.message || this.$t('serverError'),
          });
        }
      } catch (error) {
        console.error(error);
        this.$notify({
          type: 'error',
          title: this.$t('serverError'),
        });
      } finally {
        this.importing = false;
      }
    },

    /** User clicks "Back" in staging dialog — return to import dialog */
    closeStagingGoBack() {
      this.stagingDialogOpen = false;
      this.stagingData = [];
      this.importDialog = true;
    },

    /** User closes staging dialog entirely */
    handleCancel() {
      this.stagingDialogOpen = false;
      this.stagingData = [];
      this.pendingFile = null;
      this.pendingCallback = null;
    },

    restartProcess() {
      this.bulkImportDetail = null;
      this.importing = false;
      this.validating = false;
      this.tab = 'import';
      this.stagingData = [];
      this.pendingFile = null;
      this.pendingCallback = null;
    },
  },
};
</script>